import "./SurgicalPricingModal.scss";

import ButtonIcon from "components/ButtonIcon/ButtonIcon";
import { MaskedInput, Select } from "components/inputs";
import debounce from "lodash/debounce";
import { useMemo, useState } from "react";
import type { FieldErrors } from "react-hook-form";
import type { CPTCodeSelectOption } from "services/codes-api";
import {
  buildCPTCodeSelectedOption,
  mapCPTCodeResultToSelectOption,
  useGetCPTCodesByInputQuery,
} from "services/codes-api";
import { formatCPTCodeOptionLabel } from "utils/cptCodeOptionLabel";

type SurgicalPricingCptCode = {
  code: string;
  description: string;
  price: number;
};

type CPTCodeOption = CPTCodeSelectOption;

type SurgicalPricingFormData = {
  cpt_codes: SurgicalPricingCptCode[];
  surgery_name: string;
  state: string;
  zip_code: string;
  mmc: string;
  procedure_type: "OUTPATIENT" | "INPATIENT";
  anesthesia_code: string;
  base_unit: number | null;
  total_units: number | null;
  conversion_factor: number | null;
  adj_apc_payment: boolean;
  ms_drg_code: string;
  ms_drg_description: string;
  ms_drg_base_price: number | null;
  state_multiplier: number;
  organization: number;
};

type CPTCodeSectionProps = {
  cptCodes: SurgicalPricingCptCode[];
  errors: FieldErrors<SurgicalPricingFormData>;
  onAdd: () => void;
  onRemove: (index: number) => void;
  onChange: (index: number, updates: Partial<SurgicalPricingCptCode>) => void;
};

const MAX_CPT_SEARCH_RESULTS = 25;

type CPTCodeRowProps = {
  cptCode: SurgicalPricingCptCode;
  index: number;
  errors: FieldErrors<SurgicalPricingFormData>;
  onRemove: (index: number) => void;
  onChange: (index: number, updates: Partial<SurgicalPricingCptCode>) => void;
};

const CPTCodeRow = ({
  cptCode,
  index,
  errors,
  onRemove,
  onChange,
}: CPTCodeRowProps) => {
  const [codeSearchTerm, setCodeSearchTerm] = useState<string | null>(null);
  const [descSearchTerm, setDescSearchTerm] = useState<string | null>(null);

  const debouncedSetCodeSearchTerm = useMemo(
    () =>
      debounce((value: string | null) => {
        setCodeSearchTerm(value);
      }, 500),
    [],
  );

  const debouncedSetDescSearchTerm = useMemo(
    () =>
      debounce((value: string | null) => {
        setDescSearchTerm(value);
      }, 500),
    [],
  );

  const { data: codeResults, isFetching: isCodeFetching } =
    useGetCPTCodesByInputQuery(
      {
        code__istartswith: codeSearchTerm?.match(/^\d/) ? codeSearchTerm : null,
        description__icontains: !codeSearchTerm?.match(/^\d/)
          ? codeSearchTerm
          : null,
        limit: MAX_CPT_SEARCH_RESULTS,
      },
      {
        skip: !codeSearchTerm,
      },
    );

  const { data: descResults, isFetching: isDescFetching } =
    useGetCPTCodesByInputQuery(
      {
        description__icontains: descSearchTerm,
        limit: MAX_CPT_SEARCH_RESULTS,
      },
      {
        skip: !descSearchTerm,
      },
    );

  const codeOptions = useMemo(() => {
    if (!codeResults?.results) return [];

    return codeResults.results.map(mapCPTCodeResultToSelectOption);
  }, [codeResults]);

  const descOptions = useMemo(() => {
    if (!descResults?.results) return [];

    return descResults.results.map(mapCPTCodeResultToSelectOption);
  }, [descResults]);

  const currentCode = buildCPTCodeSelectedOption(
    cptCode.code,
    cptCode.description,
    codeOptions,
  );

  const currentDesc = buildCPTCodeSelectedOption(
    cptCode.code,
    cptCode.description,
    descOptions,
    cptCode.description,
  );

  return (
    <>
      <Select
        name={`cpt_code_${index}`}
        errors={errors}
        placeholder="CPT code"
        noOptionsMessage={({ inputValue }) =>
          !inputValue
            ? "Begin typing a CPT code..."
            : isCodeFetching
              ? "Searching..."
              : "No matches found."
        }
        options={codeOptions}
        value={currentCode}
        classNames="cpt-code-input"
        tooltip={
          currentCode ? `${cptCode.code} - ${cptCode.description}` : undefined
        }
        formatOptionLabel={(option, meta) =>
          formatCPTCodeOptionLabel(
            option as CPTCodeSelectOption,
            meta,
            true,
            "stacked",
          )
        }
        onInputChange={(value: string) => debouncedSetCodeSearchTerm(value)}
        onChange={(o: CPTCodeOption) => {
          if (!o) {
            onChange(index, {
              code: "",
              description: "",
            });
            return;
          }
          onChange(index, {
            code: o.code,
            description: o.description,
          });
        }}
      />

      <Select
        name={`cpt_description_${index}`}
        errors={errors}
        placeholder="Search description"
        noOptionsMessage={({ inputValue }) =>
          !inputValue
            ? "Begin typing a description..."
            : isDescFetching
              ? "Searching..."
              : "No matches found."
        }
        options={descOptions}
        value={currentDesc}
        classNames="cpt-code-description"
        tooltip={
          currentDesc ? `${cptCode.code} - ${cptCode.description}` : undefined
        }
        formatOptionLabel={(option, meta) =>
          formatCPTCodeOptionLabel(
            option as CPTCodeSelectOption,
            meta,
            true,
            "stacked",
          )
        }
        onInputChange={(value: string) => debouncedSetDescSearchTerm(value)}
        onChange={(o: CPTCodeOption) => {
          if (!o) {
            onChange(index, {
              code: "",
              description: "",
            });
            return;
          }
          onChange(index, {
            code: o.code,
            description: o.description,
          });
        }}
      />

      <MaskedInput
        mask="dollar"
        value={cptCode.price.toString()}
        onChange={(e: React.ChangeEvent<HTMLInputElement>) =>
          onChange(index, { price: parseFloat(e.target.value) || 0 })
        }
        name={`cpt_price_${index}`}
        errors={errors}
      />
      <figure>
        <ButtonIcon
          onClick={() => onRemove(index)}
          kind="neutral"
          size="small"
          className="delete"
        >
          delete
        </ButtonIcon>
      </figure>
    </>
  );
};

const CPTCodeSection = ({
  cptCodes,
  errors,
  onAdd,
  onRemove,
  onChange,
}: CPTCodeSectionProps) => {
  return (
    <div className="cpt-codes-section">
      <div className="header-primary">CPT Code</div>
      <div className="header-primary">Description</div>
      <div className="header-primary">Pricing</div>
      <div className="header-primary" style={{ justifyContent: "unset" }}>
        <ButtonIcon onClick={onAdd} kind="neutral" size="small">
          add_circle
        </ButtonIcon>
      </div>

      {cptCodes.map((cptCode, index) => (
        <CPTCodeRow
          key={index}
          cptCode={cptCode}
          index={index}
          errors={errors}
          onRemove={onRemove}
          onChange={onChange}
        />
      ))}
    </div>
  );
};

export type { SurgicalPricingCptCode };
export default CPTCodeSection;
